import { Annotation } from './../models/lesson';
import { NgbModal, NgbModalOptions, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { EventEmitter, Injectable, Input, Output } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams, HttpRequest, HttpResponse } from '@angular/common/http';

import { LoginService } from './login.service';
import { GeneralService } from './general.service';

import { Product } from '../models/product';
import { Lesson } from '../models/lesson';
import { Timeline } from '../models/timeline';
import { Comment } from '../models/comment';

import { Category } from '../models/category';
import { Certificate } from '../models/certificate';
import { Purchase } from '../models/purchase';
import { Subscription } from '../models/subscription';

import { PaywallComponent } from './../../timeline/lessons/paywall/paywall.component';
import { ModalDialogComponent, ModalDialogButton } from '../../shared/dialogs/modal-dialog/modal-dialog.component';
import { EmailDialogComponent } from '../../timeline/lessons/dialogs/dialog.email.component';

import { NavigationExtras, Router } from '@angular/router';
import { Observable } from 'rxjs';

import { environment } from '../../../environments/environment';

import * as CryptoJS from 'crypto-js';
import { BehaviorSubject } from 'rxjs';
import { AppUser } from '../models/app.user';
import { Home } from '../models/home';
import { addListener, launch, stop } from "devtools-detector";


@Injectable()
export class TimelineService {

  private URL: string = environment.urlbase;
  private headers: HttpHeaders;

  private exists(object: Object): boolean {
    return object !== null && object !== undefined && object != '';
  }

  public timeLine$ = new BehaviorSubject(null);
  public menu$ = new BehaviorSubject(null);
  public favorite$ = new BehaviorSubject(null);
  public killDevTools$ = new BehaviorSubject<boolean>(false);

  private _cachedProduct: Product | null = null;

  cacheProduct(product: Product): void {
    this._cachedProduct = product;
  }

  getCachedProduct(productId: number): Product | null {
    if (this._cachedProduct && this._cachedProduct.id === productId) {
      const cached: Product = this._cachedProduct;
      this._cachedProduct = null;
      return cached;
    }
    this._cachedProduct = null;
    return null;
  }


  @Input() commentsPerPage: number = 40;
  @Output() reload: EventEmitter<void> = new EventEmitter();

  pathBaseTranslate: string = 'timeline.lessons';
  constructor(
    private http: HttpClient,
    private loginService: LoginService,
    private generalService: GeneralService,
    private router: Router
  ) {
    this.headers = this.loginService.getHeaders();
    this.loginService.loggedUser$.subscribe(
      (user: AppUser): void => {
        if (environment.production && !user?.is_producer && !environment.disableDevToolsDetector) {
          addListener(
            (isOpen: boolean): void => {
              this.killDevTools$.next(isOpen);
            }
          );
          launch();
        }
        if (environment.production && user?.is_producer) {
          stop();
          this.killDevTools$.next(false);
        }
    });
  }


  /* ===================================================================
     ---------- M A I N / P R O D U C T S   F U N C T I O N S ----------
     =================================================================== */

  timeline(): Observable<Timeline> {
    this.headers = this.loginService.getHeaders();
    const timelineExt: string = 'timeline' + this.generalService.authExtension();
    return this.http.get<Timeline>(this.URL + timelineExt, {headers: this.headers});
  }

  product(model: Product | number): Observable<Product> {
    this.headers = this.loginService.getHeaders();
    const productsExt: string = 'products/' + (typeof(model) == 'number' ? model : model.id) + this.generalService.authExtension();
    return this.http.get<Product>(this.URL + productsExt, {headers: this.headers});
  }

  search(target: string): Observable<{searchResult: Category[]}> {
    this.headers = this.loginService.getHeaders();
    const searchExt: string = 'search' + this.generalService.authExtension();
    let options: {headers?: HttpHeaders, params?: HttpParams | {[param: string]: string | string[]}} = {headers: this.headers};
    let queryParams = '';
    if (target) queryParams = `?q=${encodeURIComponent(target)}`;
    return this.http.get<{searchResult: Category[]}>(`${this.URL}${searchExt}${queryParams}`, options);
  }

  countSearchResult(type: "lesson" | "product", search_id: number): Observable<void> {
    this.headers = this.loginService.getHeaders();
    const searchExt: string = 'search/update';
    return this.http.post<void>(this.URL + searchExt, {type: type, search_id: search_id});
  }

  myCertificates(): Observable<Certificate[]> {
    this.headers = this.loginService.getHeaders();
    const certificatesExt: string = 'certificates';
    return this.http.get<Certificate[]>(this.URL + certificatesExt, {headers: this.headers});
  }

  myPurchases(): Observable<Purchase[]> {
    this.headers = this.loginService.getHeaders();
    const purchasesExt: string = 'purchases' + this.generalService.authExtension();
    return this.http.get<Purchase[]>(this.URL + purchasesExt, {headers: this.headers});
  }

  mySubscriptions(): Observable<Subscription[]> {
    this.headers = this.loginService.getHeaders();
    const purchasesExt: string = 'subscriptions' + this.generalService.authExtension();
    return this.http.get<Subscription[]>(this.URL + purchasesExt, {headers: this.headers});
  }

  /* =====================================================
     ---------- L E S S O N   F U N C T I O N S ----------
     =====================================================*/

  lesson(model: Lesson | number): Observable<Lesson> {
    this.headers = this.loginService.getHeaders();
    const lessonsExt: string = 'lessons/' + (typeof(model) == 'number' ? model : model.id) + this.generalService.authExtension();
    return this.http.get<Lesson>(this.URL + lessonsExt, {headers: this.headers});
  }

  finish(model: Lesson | number, progress: number = 1, time: number = 0, token: string = null): Observable<any> {
    const newHttpHeaders: HttpHeaders = this.loginService.getHeaders(token);
    const URL: string = environment.urlbase + 'lessons/' + (typeof(model) == 'number' ? model : model.id) + '/seen';
    const progressPercent = progress * 100;
    return this.http.request(new HttpRequest('POST', URL, {percent: progressPercent, time_seconds: Math.floor(time)}, {headers: newHttpHeaders}));
  }

  rate(model: Lesson, rating: number, token: string = null): Observable<any> {
    const newHeader: HttpHeaders = this.loginService.getHeaders(token);
    const URL: string = environment.urlbase + 'lessons/' + (typeof(model) == 'number' ? model : model.id) + '/rate';
    return this.http.request(new HttpRequest('POST', URL, {rating: rating}, {headers: newHeader}));
  }

  comments(model: Lesson | number, page: number): Observable<Comment[]> {
    this.headers = this.loginService.getHeaders();
    const commentExt: string = 'lessons/' + (typeof(model) == 'number' ? model : model.id) + '/comment';
    const params: {[param: string]: string} = {limit: this.commentsPerPage.toString(), offset: ((page - 1) * this.commentsPerPage).toString()};
    return this.http.get<Comment[]>(this.URL + commentExt, {headers: this.headers, params: params});
  }

  comment(id: number, comment: string, reply: number = null, imageKey: string = null, anonymous: boolean = false): Observable<Comment> {
    this.headers = this.loginService.getHeaders();
    const commentExt: string = 'lessons/' + id + '/comment';
    const commentObj: Object = reply !== null ? {comment: comment, comment_reply_id: reply, image_key: imageKey, anonymous: anonymous} : {comment: comment, image_key: imageKey, anonymous: anonymous};
    return this.http.post<Comment>(this.URL + commentExt, commentObj, {headers: this.headers});
  }

  editComment(id: number, newComment: string, idComment: number): Observable<Comment> {
    this.headers = this.loginService.getHeaders();
    const commentExt: string = 'lessons/' + id + '/comment/' + idComment;
    const commentObj: Object = {comment: newComment};
    return this.http.put<Comment>(this.URL + commentExt, commentObj, {headers: this.headers});
  }

  deleteComment(id: number, idComment: number): Observable<Comment> {
    this.headers = this.loginService.getHeaders();
    const commentExt: string = 'lessons/' + id + '/comment/' + idComment;
    return this.http.delete<Comment>(this.URL + commentExt, {headers: this.headers});
  }

  myList(): Observable<Lesson[]> {
    this.headers = this.loginService.getHeaders();
    const favoritesExt: string = 'lessons/favorites';
    return this.http.get<Lesson[]>(this.URL + favoritesExt, {headers: this.headers});
  }

  addToMyList(model: Lesson | number): Observable<void> {
    this.headers = this.loginService.getHeaders();
    const favoriteExt: string = 'lessons/' + (typeof(model) == 'number' ? model : model.id) + '/favorite';
    return this.http.post<void>(this.URL + favoriteExt, null, {headers: this.headers});
  }

  removeFromMyList(model: Lesson | number): Observable<void> {
    this.headers = this.loginService.getHeaders();
    const favoriteExt: string = 'lessons/' + (typeof(model) == 'number' ? model : model.id) + '/favorite';
    return this.http.delete<void>(this.URL + favoriteExt, {headers: this.headers});
  }

  rating(model: Lesson | number, rating_avg: number): Observable<Lesson> {
    this.headers = this.loginService.getHeaders();
    const lessonsExt: string = 'lessons/' + (typeof(model) == 'number' ? model : model.id) + "/rating";
    return this.http.post<Lesson>(this.URL + lessonsExt, {rating: rating_avg} , {headers: this.headers});
  }

  lessonQuizAi(id: number): Observable<Lesson> {
    this.headers = this.loginService.getHeaders();
    const url: string = `${this.URL}ai/lesson-quiz/${id}`
    return this.http.get<Lesson>(url, {headers: this.headers});
  }

  validateLesson(model: Lesson, modal: NgbModal, ngbOptions: NgbModalOptions, reload: (reload: boolean) => void): boolean {
    if (!model?.action) {
      return false;
    }
    if (model.action.type === 'PAYWALL') {
      setTimeout((): void => {
        const activeModal: NgbModalRef = modal.open(PaywallComponent, ngbOptions);
        activeModal.componentInstance.model = model.action.url;
        activeModal.componentInstance.contentId = model.id?.toString();
        activeModal.componentInstance.contentType = 'lesson';
        activeModal.result.then(
          (data: string): void => {
            if (data == 'reload') reload(true);
          },
          (): void => { },
        );
      }, 0);
      return false;
    } else if (model.action.type == 'EMAIL_CONFIRMATION') {
      const activeModal: NgbModalRef = modal.open(EmailDialogComponent, ngbOptions);
      this.loginService.loggedUser$.subscribe(
        (user: AppUser): void => {
          activeModal.componentInstance.model = user;
        }
      );
      activeModal.result.then(
        (): void => {
          reload(true);
        },
        (): void => { }
      );
      return false;
    } else if (model.action.type == 'WAITING') {
      setTimeout((): void => {
        const waitingOptions: NgbModalOptions = {
          ...ngbOptions,
          windowClass: 'about-modal-wrapper',
          backdropClass: 'about-modal-backdrop',
          size: 'sm',
        };
        const defaults = this.getWaitingLessonDefaultTexts();
        const title: string = this.exists(model.waiting_title) ? model.waiting_title : defaults.waiting_title;
        const subtitle: string = this.exists(model.waiting_subtitle) ? model.waiting_subtitle : defaults.waiting_subtitle;
        const message: string = subtitle.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
        const closeLabel: string = this.generalService.getTranslate('timeline.lessons.dialog-waiting.closeLabel');
        const buttons: ModalDialogButton[] = [
          { label: closeLabel, style: 'primary', action: 'dismiss' },
        ];
        const activeModal: NgbModalRef = modal.open(ModalDialogComponent, waitingOptions);
        activeModal.componentInstance.icon = 'schedule';
        activeModal.componentInstance.title = title;
        activeModal.componentInstance.message = message;
        activeModal.componentInstance.buttons = buttons;
        activeModal.result.then(
          (): void => { },
          (): void => { },
        );
      }, 0);
      return false;
    } else if (model.action.type == 'REGISTER') {
      const extras: NavigationExtras = { queryParams: { redirect: encodeURIComponent(this.router.url) }};
      this.router.navigate(['login'], extras);
      return false;
    }

    return model.action.can_access;
  }

  annotation(model: Lesson | number, annotation: string): Observable<Annotation> {
    this.headers = this.loginService.getHeaders();
    const lessonsExt: string = 'lessons/' + (typeof(model) == 'number' ? model : model.id) + "/annotation";
    return this.http.post<Annotation>(this.URL + lessonsExt, {annotation: annotation} , {headers: this.headers});
  }

  myAnnotations(): Observable<Annotation[]>{
    this.headers = this.loginService.getHeaders();
    return this.http.get<Annotation[]>(`${this.URL}annotation`, {headers: this.headers})
  }

  addToFavorite(model: Product | number):Observable<any>{
    this.headers = this.loginService.getHeaders();
    return this.http.post<any>(`${this.URL}products/${(typeof(model) == 'number' ? model : model.id)}/favorite`, {}, {headers: this.headers})
  }

  deleteFavorite(model: Product | number):Observable<any>{
    this.headers = this.loginService.getHeaders();
    return this.http.delete<any>(`${this.URL}products/${(typeof(model) == 'number' ? model : model.id)}/favorite`, {headers: this.headers})
  }

  restartProduct(id: number): Observable<any>{
    this.headers = this.loginService.getHeaders();
    return this.http.post<any>(`${this.URL}products/${id}/cleanseens`, {}, {headers: this.headers})
  }


  /* =================================================
     ---------- F I L E   F U N C T I O N S ----------
     =================================================*/

  decryptFileUrl(encryptedURL: string): string {
    let decryptedURL: string = '';
    const key: string = this.loginService.getDecToken();
    var decodedEncryptedURL: string = CryptoJS.enc.Base64.parse(encryptedURL).toString(CryptoJS.enc.Utf8);
    const laravelJSON: {iv: string, value: string} = JSON.parse(decodedEncryptedURL);
    const iv: CryptoJS.lib.WordArray = CryptoJS.enc.Base64.parse(laravelJSON.iv);
    const value: string = laravelJSON.value;
    var decryptedBin: CryptoJS.lib.WordArray = CryptoJS.AES.decrypt(
      CryptoJS.enc.Utf8.parse(value).toString(CryptoJS.enc.Utf8),
      CryptoJS.enc.Utf8.parse(key),
      {iv: iv, mode: CryptoJS.mode.CBC, padding: CryptoJS.pad.Pkcs7}
    );
    try {
      decryptedURL = CryptoJS.enc.Utf8.stringify(decryptedBin);
    } catch(e1) {
      try {
        decryptedURL = decryptedBin.toString(CryptoJS.enc.Utf8);
      } catch (e2) {

      }
    }
    let laravelFormatdecryptedURL: string[] = decryptedURL.toString().split(':');
    if (laravelFormatdecryptedURL.length > 3) {
      laravelFormatdecryptedURL.splice(0, 2);
      decryptedURL = laravelFormatdecryptedURL.join(':').replace(/\"/g, '').replace(/\;/g, '');
    }
    return decryptedURL;
  }



  isURL(urlString: string): boolean {
    var pattern: RegExp = /(ftp|http|https):\/\/(\w+:{0,1}\w*@)?(\S+)(:[0-9]+)?(\/|\/([\w#!:.?+=&%@!\-\/]))?/
    return pattern.test(urlString);
  }

  download(url: string): Observable<HttpResponse<Blob>> {
    return this.http.get(url, {headers: this.headers, observe: 'response', responseType: 'blob'});
  }

  isExcel(type: string): boolean {
    return type.replace('officedocument', '').replace('sheet', '') != type;
  }

  downloadExcel(data: HttpResponse<Blob>, done: Function): void {
    (data.body as any).arrayBuffer().then(
      (arrayBuffer: ArrayBuffer): void => {
        const fileBlob: Blob = new Blob([arrayBuffer], {type: data.body.type});
        let anchor: HTMLAnchorElement = document.createElement("a");
        anchor.href = window.URL.createObjectURL(fileBlob);
        anchor.download = '';
        anchor.click();
        done();
      }
    )
  }

  /* =======================================================
     ---------- G E N E R A L   F U N C T I O N S ----------
     =======================================================*/

  async validateChallenge(product: Product, modal: NgbModal, ngbModalOptions: NgbModalOptions): Promise<void> {
    if (product.type === 'challenge') {
      const { title, desc } = await this.getNotOpenInAppDefaultTexts('challenge');
      const challengeOptions: NgbModalOptions = {
        ...ngbModalOptions,
        windowClass: 'about-modal-wrapper',
        backdropClass: 'about-modal-backdrop',
        size: 'sm',
      };
      const message: string = desc.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
      const closeLabel: string = this.generalService.getTranslate('timeline.lessons.dialog-waiting.closeLabel');
      const buttons: ModalDialogButton[] = [
        { label: closeLabel, style: 'primary', action: 'dismiss' },
      ];
      const activeModal: NgbModalRef = modal.open(ModalDialogComponent, challengeOptions);
      activeModal.componentInstance.icon = 'info';
      activeModal.componentInstance.title = title;
      activeModal.componentInstance.message = message;
      activeModal.componentInstance.buttons = buttons;
      activeModal.result.then(
        (): void => {},
        (): void => {}
      );
    }
  }

  getWaitingLessonDefaultTexts(): {waiting_title: string, waiting_subtitle: string} {
    return {
      waiting_title: 'Conteúdo indisponível',
      waiting_subtitle: 'Aguarde.<br>Este conteúdo será liberado em breve para você.'
    }
  }

  async getNotOpenInAppDefaultTexts(context?:string): Promise<{ title: string, desc: string }> {
    const imageDownloadAppleStore: string = "/assets/images/img-download-apple.webp";
    const imageDownloadGoogleStore: string = "/assets/images/img-download-google.webp";

    let desc:string = '';

    if (context === 'challenge') {
      desc = `${this.getTranslate('.dialog-not-open-in-app-challenge.title')} ${this.getTranslate('.dialog-not-open-in-app-challenge.subtitle')} ${environment.appname} ${this.getTranslate('.dialog-not-open-in-app-challenge.complemento')} Android ou iOs`;
    }
    else {
      desc = `${this.getTranslate('.dialog-not-open-in-app.title')} ${this.getTranslate('.dialog-not-open-in-app.subtitle')} ${environment.appname} ${this.getTranslate('.dialog-not-open-in-app.complemento')} Android ou iOs`;
    }

    desc += '<br><br><br>';

    try {
      const home: Home = await this.generalService.home();
      const linkAppleStore: string = this.generalService.getStoredHome().ios_url;
      const linkGoogleStore: string = this.generalService.getStoredHome().android_url;

      if (this.exists(linkAppleStore)) {
        desc += '<a target="_blank" href="' + linkAppleStore + '">';
        desc += '<img width="180" class="store-button-img margin_5" src="' + imageDownloadAppleStore + '" alt="downloadApple" /></a>';
      }
      if (this.exists(linkGoogleStore)) {
        desc += '<a target="_blank" href="' + linkGoogleStore + '">';
        desc += '<img width="180" class="store-button-img margin_5" src="' + imageDownloadGoogleStore + '" alt="downloadGoogle" /></a>';
      }

      return {
        title: 'Ops!',
        desc: desc
      };

    } catch (error) {
      console.error('Erro ao obter as informações da home:', error);
    }
  }

  fancyType(type: string): string {
    if (type === null || type === undefined || type == '') return '';
    if (type == 'VIDEO') return this.getTranslate('.thumbs.videoLabel');
    if (type == 'SOUND') return this.getTranslate('.thumbs.soundLabel');
    if (type == 'FILE') return this.getTranslate('.thumbs.fileLabel');
    if (type == 'QUIZ') return this.getTranslate('.thumbs.quizLabel');
    if (type == 'EXTERNAL_URL') return this.getTranslate('.thumbs.linkLabel');
    if (type == 'HTML') return this.getTranslate('.thumbs.htmlLabel');
  }

  fancyIcon(model: Lesson, showSeenOrBlocked: boolean = true): string {
    let modelFancyIcon: string;
    if (model === null || model === undefined) return '';
    if (model.action !== null && model.action !== undefined && !model.action.can_access && showSeenOrBlocked) {
      modelFancyIcon = '<div class="lesson-icon locked-lesson"><img src="/assets/images/ic-lock-lesson.svg" class="icons-svg-thumbs"></div>';
    } else if (model.lesson_seen && showSeenOrBlocked) {
      modelFancyIcon = '<span class="lesson-icon checked-lesson"><img src="/assets/images/ic-check.svg" class="icons-svg-list"></div></span>';
    } else {
      if (model.type == 'VIDEO') {
        modelFancyIcon = '<span class="lesson-icon regular-lesson"><img src="/assets/images/ic-play-list.svg" class="icons-svg-list"></span>';
      }
      if (model.type == 'SOUND') {
        modelFancyIcon = '<span class="lesson-icon regular-lesson"><img src="/assets/images/ic-audio-list.svg" class="icons-svg-list"></span>';
      }
      if (model.type == 'FILE') {
        modelFancyIcon = '<span class="lesson-icon regular-lesson"><img src="/assets/images/ic-file.svg" class="icons-svg-list"></span>';
      }
      if (model.type == 'QUIZ') {
        modelFancyIcon = '<span class="lesson-icon regular-lesson"><img src="/assets/images/ic-quiz-list.svg" class="icons-svg-list"></span>';
      }
      if (model.type == 'EXTERNAL_URL') {
        modelFancyIcon = '<span class="lesson-icon regular-lesson"><img src="/assets/images/ic-link-list.svg" class="icons-svg-list"></span>';
      }
      if (model.type == 'HTML') {
        modelFancyIcon = '<span class="lesson-icon regular-lesson"><img src="/assets/images/ic-text-list.svg" class="icons-svg-list"></span>';
      }
    }
    return modelFancyIcon;
  }

  fancyDuration(duration: string): string {
    if (duration === null || duration === undefined || duration == '' || duration == '0') return '';
    const min: number = Math.floor(parseInt(duration) / 60);
    return ' - ' + min + ' min';
  }

  fancyRating(model: Lesson): string {
    if (model === null || model === undefined || model.rating_avg) return '';
    return `<div class="col-12 mt-2" *ngIf="model.rating_avg && this.show_ratings">
              <span class="lesson-icon regular-lesson">
                <img src="/assets/images/ic-rating-list.svg" class="icons-svg-list mr-1">
              </span>
              <span style="position: absolute; top: 2px">` + model.rating_avg + `</span>
            </div>`;
  }

  fancyHeader(model: Lesson, showRating: boolean = false): string {
    if (model === null || model === undefined) return '';
    const lessonType: string = '<span>' + this.fancyIcon(model) + this.fancyType(model.type) + '<span>';
    const lessonRating: string = showRating ? this.fancyRating(model) : '';
    const lessonDuration: string = (model.type == 'QUIZ' ?
                                   (' - ' + model.questions_count + ' quest' + (model.questions_count == 1 ? 'ão' : 'ões')) :
                                   (model.duration !== null && model.duration !== undefined ? this.fancyDuration(model.duration.toString()) : ''));
    return lessonType + lessonRating + lessonDuration

  }

  getTranslate(key: string): string {
    return this.generalService.getTranslate(`${this.pathBaseTranslate}${key}`);
  }
}
